import Typography from '@material-ui/core/Typography'
import Page from 'organization/Event/Page'
import React, {useState} from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import {Checkbox, FormControlLabel, withStyles} from '@material-ui/core'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {spacing} from 'lib/ui/theme'
import Button from '@material-ui/core/Button'
import TagSelector from 'organization/Event/Services/Apps/ConvertKit/Config/Tags/TagSelector'
import {
  ATTENDEE_CHECKED_IN,
  ATTENDEE_CREATED,
  ATTENDEE_SIGNED_WAIVER,
  IMPORT_TAG,
  Tag,
  UPDATE_TAG,
} from 'organization/Event/Services/Apps/ConvertKit/Config/Tags'
import {
  useConvertKit,
  useServices,
} from 'organization/Event/Services/ServicesProvider'
import AutocompleteFields, {
  ConvertKitField,
  convertKitEmptyField,
} from 'organization/Event/Services/Apps/ConvertKit/Config/Fields/AutocompleteFields'
import {
  ConvertKitIntegration,
  LastNameField,
  LoginField,
  PhoneField,
} from 'organization/Event/Services/Apps/ConvertKit'
import GroupsConfig from 'organization/Event/Services/Apps/ConvertKit/Config/Groups/GroupsConfig'
import {Group} from 'organization/Event/Services/Apps/ConvertKit/Config/Groups/convertKitGroup'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useToggle} from 'lib/toggle'
import InfoAlert from 'lib/ui/alerts/InfoAlert'
import ImportAttendeesButton from 'organization/Event/Services/Apps/ConvertKit/Config/ImportAttendeesButton'
import {onChangeCheckedHandler} from 'lib/dom'

export interface UpdateData {
  tags: Tag[]
  groups: Group[]
  login_field: LoginField
  phone_field: PhoneField
  last_name_field: LastNameField
  should_parse_last_name_from_first_name: boolean
}

export default function Config() {
  const convertKit = useConvertKit()
  const {save, processing, error} = useSave()

  const {flag: showingImportSuccess, toggle: toggleImportSuccess} = useToggle()
  const [importError, setImportError] = useState('')
  const clearImportError = () => setImportError('')

  const [tags, setTags] = useState<Tag[]>(convertKit.tags)
  const [groups, setGroups] = useState<Group[]>(convertKit.groups)

  const [extractLastName, setExtractLastName] = useState<boolean>(
    convertKit.should_parse_last_name_from_first_name,
  )

  const [loginField, setLoginField] = useState<LoginField>({
    id: convertKit.login_field_id ?? '',
    name: convertKit.login_field_name ?? '',
    key: convertKit.login_field_key ?? '',
    label: convertKit.login_field_label ?? '',
  })

  const [lastNameField, setLastNameField] = useState<LastNameField>({
    id: convertKit.last_name_field_id ?? '',
    name: convertKit.last_name_field_name ?? '',
    key: convertKit.last_name_field_key ?? '',
    label: convertKit.last_name_field_label ?? '',
  })

  const [phoneField, setPhoneField] = useState<PhoneField>({
    id: convertKit.phone_field_id ?? '',
    name: convertKit.phone_field_name ?? '',
    key: convertKit.phone_field_key ?? '',
    label: convertKit.phone_field_label ?? '',
  })

  const updateTag = (tag: Tag) => {
    const updated = tags.map((t) => {
      const isTarget = t.id === tag.id

      if (isTarget) {
        return tag
      }

      return t
    })

    setTags(updated)
  }

  const changeLoginField = (field: ConvertKitField | null) => {
    setLoginField(field ?? convertKitEmptyField)
  }

  const changeLastNameField = (field: ConvertKitField | null) => {
    setLastNameField(field ?? convertKitEmptyField)
  }

  const changePhoneField = (field: ConvertKitField | null) => {
    setPhoneField(field ?? convertKitEmptyField)
  }

  const changeExtractLastName = (value: boolean) => {
    setExtractLastName(value)

    if (value === true) {
      setLastNameField(convertKitEmptyField) // reset last name field
    }
  }

  const handleSave = () => {
    const data: UpdateData = {
      tags,
      groups,
      login_field: loginField,
      phone_field: phoneField,
      last_name_field: lastNameField,
      should_parse_last_name_from_first_name: extractLastName,
    }

    save(data)
  }

  const tagForType = (type: Tag['type']) => {
    const target = tags.find((t) => t.type === type)
    if (!target) {
      throw new Error(`Unknown tag type: ${type}`)
    }

    return target
  }

  return (
    <Page>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Typography variant="h4">Kit</Typography>
          </Grid>
          <StyledGrid item md={6}>
            <ImportAttendeesButton
              onSuccess={toggleImportSuccess}
              onError={setImportError}
            />
          </StyledGrid>
        </Grid>
      </Box>

      <ErrorAlert onClose={clearImportError}>{importError}</ErrorAlert>
      <InfoAlert showing={showingImportSuccess}>
        Import successfully started. Depending on the number of subscribers.
      </InfoAlert>

      <Box mb={3}>
        <Box mb={2}>
          <Typography variant="h5">
            Attendee Import or Delete via Tag
          </Typography>
        </Box>
        <TagSelector tag={tagForType(IMPORT_TAG)} onChange={updateTag} />
        <em>
          If an attendee with the given email already exists, obv.io will update
          the other attributes.
        </em>
      </Box>

      <Box mb={3}>
        <Box mb={2}>
          <Typography variant="h5">Update attendee via Tag</Typography>
        </Box>
        <TagSelector tag={tagForType(UPDATE_TAG)} onChange={updateTag} />
        <em>Sync attendee to OBVio when tag is applyed.</em>
      </Box>

      <Box mb={2}>
        <Typography variant="h5">Login URL Field</Typography>
        <em>
          Select the Custom Field in Kit to save your attendee's Login URL.
        </em>
      </Box>

      <AutocompleteFields
        onChange={changeLoginField}
        inputVariant={'outlined'}
        value={loginField}
      />

      <Box mb={2}>
        <Typography variant="h5">Last Name Field</Typography>
        <em>
          Select the Custom Field in Kit to save your attendee's Last Name.
        </em>
        <br />
        <FormControlLabel
          label="Extract from FIRST NAME field."
          control={
            <Checkbox
              disableRipple
              checked={extractLastName}
              onChange={onChangeCheckedHandler(changeExtractLastName)}
              inputProps={{
                'aria-label': 'extract from first name',
              }}
            />
          }
        />
      </Box>

      <AutocompleteFields
        disabled={extractLastName}
        onChange={changeLastNameField}
        inputVariant={'outlined'}
        value={lastNameField}
      />

      <Box mb={2}>
        <Typography variant="h5">Phone Field</Typography>
        <em>
          Select the Custom Field in Kit to import attendee Phone from it.
        </em>
      </Box>

      <AutocompleteFields
        onChange={changePhoneField}
        inputVariant={'outlined'}
        value={phoneField}
      />

      <Box mb={3}>
        <Typography variant="h5">Action Tags</Typography>
        <em>
          The following tags can be applied in ActiveCampaign when an attendee
          takes the specific action. (optional)
        </em>
      </Box>

      <TagSelector tag={tagForType(ATTENDEE_CREATED)} onChange={updateTag} />
      <TagSelector tag={tagForType(ATTENDEE_CHECKED_IN)} onChange={updateTag} />
      <TagSelector
        tag={tagForType(ATTENDEE_SIGNED_WAIVER)}
        onChange={updateTag}
      />

      <Box mb={3}>
        <Typography variant="h5">Groups</Typography>
        <em>
          Select additional custom fields you wish to import into your Obvio
          event. (optional)
        </em>
      </Box>
      <Box mb={5}>
        <GroupsConfig onChange={setGroups} groups={groups} />
      </Box>

      <ErrorAlert>{error}</ErrorAlert>
      <SaveButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        aria-label="save"
        onClick={handleSave}
        disabled={processing}
      >
        Save
      </SaveButton>
    </Page>
  )
}

function useSave() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const [processing, setProcessing] = useState<boolean>(false)
  const {update: updateIntegration} = useServices()
  const [error, setError] = useState<string | null>(null)

  const save = (data: UpdateData) => {
    setProcessing(true)
    setError('')

    client
      .put<ConvertKitIntegration>(
        api(`/events/${event.id}/integrations/convert_kit`),
        data,
      )
      .then(updateIntegration)
      .catch((e) => setError(e.message))
      .finally(() => {
        setProcessing(false)
      })
  }

  return {
    processing,
    error,
    save,
  }
}

const SaveButton = withStyles({
  root: {
    marginBottom: spacing[5],
  },
})(Button)

const StyledGrid = withStyles({
  root: {
    display: 'flex',
    justifyContent: 'end',
  },
})(Grid)
